import EnhancedTable from '@common/components/Table/EnhancedTable'
import EnhancedTableBodyTaskPool from '@reader_task_list_view/components/EnhancedTableBodyTaskPool'
import { HeadCell } from '@common/components/Table/table.types'
import { ReadTask } from '@common/config/api/client'
import { ROW_PER_PAGE_OPTIONS } from '@common/constants/table'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import { useParams } from 'react-router-dom'
import { useSearch } from '@common/hooks/useSeach'
import { useReadTasksManagementHandlers } from '@features/reader_task_list_iam_view/pages/hooks/useReadTasksManagementHandlers'
import { Stack, Typography, TableBody, TableRow, TableCell } from '@mui/material'
import { SPACING } from '@common/theme/spacing'

interface ReaderTaskListTableTaskPoolProps {
  subjectId?: string
  isRawDataEnabled?: boolean
  isOphthoEnabled?: boolean
}

const headCells: readonly HeadCell<ReadTask>[] = [
  { id: 'task_id', disablePadding: true, label: 'Task pseudo ID', disableSort: false },
  { id: 'blind_code', disablePadding: false, label: 'Blind code', disableSort: false },
  { id: 'task_type', disablePadding: false, label: 'Type', disableSort: false },
  { id: 'priority', disablePadding: false, label: 'Priority', disableSort: false },
  { id: 'created_at', disablePadding: false, label: 'Created at', disableSort: false },
  { id: 'due_date', disablePadding: false, label: 'Due date', disableSort: false },
  { id: 'status', disablePadding: false, label: 'Status', disableSort: false },
]

function EmptyReaderTaskPoolTable({ searchValue }: { searchValue?: string }) {
  return (
    <Stack spacing={SPACING.minSpacing} alignItems="center" sx={{ py: SPACING.spacing2xl }}>
      <Typography variant="h6">{searchValue ? 'No matching tasks found' : 'No tasks available in pool'}</Typography>
      <Typography variant="body2" color="text.secondary">
        {searchValue
          ? 'Try adjusting your search criteria or clear the search field'
          : 'There are currently no unassigned reader tasks in the pool'}
      </Typography>
    </Stack>
  )
}

export default function ReaderTaskListTableTaskPool({
  subjectId,
  isRawDataEnabled,
  isOphthoEnabled,
}: ReaderTaskListTableTaskPoolProps) {
  const { studyId } = useParams()
  const { value } = useSearch()
  const { user } = useExtendedAuth()
  const {
    order,
    orderBy,
    page,
    paginationParams,
    data,
    error,
    isLoading,
    handleRequestSort,
    handlePageChange,
    handleRowsPerPageChange,
  } = useReadTasksManagementHandlers({
    category: 'task_pool',
    studyId: studyId!,
    subjectId,
    searchValue: value,
  })

  if (isLoading) {
    return <div>Loading...</div>
  }
  if (error) {
    return <div>Error: {error.message}</div>
  }

  const hasPoolTasks = data?.data?.items && data.data.items.length > 0

  return (
    <EnhancedTable<ReadTask>
      rows={data?.data?.items || []}
      rowCount={-1}
      headCells={headCells}
      order={order}
      orderBy={orderBy.split('.')[0] as keyof ReadTask}
      page={page}
      rowsPerPage={paginationParams.limit || 0}
      rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
      next_cursor={data?.data?.pagination.next_cursor}
      previous_cursor={data?.data?.pagination.previous_cursor}
      onRequestSort={handleRequestSort}
      onChangePage={handlePageChange}
      onChangeRowsPerPage={handleRowsPerPageChange}
    >
      {hasPoolTasks ? (
        <EnhancedTableBodyTaskPool
          rows={data?.data?.items || []}
          order={order}
          orderBy={orderBy}
          authedUserToken={user?.access_token}
          authedUserId={user?.profile.sub}
          isRawDataEnabled={isRawDataEnabled || false}
          isOphthoEnabled={isOphthoEnabled || false}
        />
      ) : (
        <TableBody>
          <TableRow>
            <TableCell colSpan={headCells.length}>
              <EmptyReaderTaskPoolTable searchValue={value} />
            </TableCell>
          </TableRow>
        </TableBody>
      )}
    </EnhancedTable>
  )
}
